import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Carousel, Checkbox, Icon, Input, Modal, Tooltip} from 'antd';
import {isModalOpened} from '../../redux/selectors/nav';
import {makeAction} from '../../redux/actions/makeAction';
import {
  CLOSE_MODAL,
  OPEN_MODAL,
  REQUEST_ACTIVATION_CODE,
  SIGN_UP,
  VALIDATE_CODE,
} from '../../redux/actions/actionTypes';
import {LoginModalId, API_CODE_LENGTH, SignUpModalId, USER_AGREEMENT_MODAL_ID, VerificationCodeType} from '../../services/constants';
import VerificationCodeForm from './common/VerificationCodeForm';
import FormError from '../FormError';
import {authErrors} from '../../redux/selectors/error';
import {withTranslation, Trans} from 'react-i18next';
import TranslatedText from '../TranslatedText';
import {ReactComponent as CloseIcon} from '../../assets/img/close.svg';
import {ReactComponent as ArrowLeft} from '../../assets/img/arrowLeft.svg';
import {ReactComponent as ArrowRight} from '../../assets/img/arrowRight.svg';

const defaultState = {
  email: '',
  code: _.range(API_CODE_LENGTH).map(() => ' ').join(''),
  stepOneInProgress: false,
  stepThreeInProgress: false,
  agreed: false,
  code_invalid: false,
};

class SignUpModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {...defaultState};

    this.stepOneSubmit = this.stepOneSubmit.bind(this);
    this.stepTwoSubmit = this.stepTwoSubmit.bind(this);
    this.stepTwoResend = this.stepTwoResend.bind(this);
    this.stepThreeSubmit = this.stepThreeSubmit.bind(this);
    this.onCodeChange = this.onCodeChange.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onUserAgreementClick = this.onUserAgreementClick.bind(this);
    this.carousel = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (Object.keys(this.props.errors).length > Object.keys(prevProps.errors).length) {
      this.setState({
        stepOneInProgress: false,
        stepThreeInProgress: false,
      });
    }
  }

  stepOneSubmit(event) {
    event.preventDefault();
    this.setState({stepOneInProgress: true});
    this.props.requestActivationCode({
      ...Object.fromEntries(new FormData(event.target)),
      agreed: this.state.agreed,
      callback: () => {
        this.setState({stepOneInProgress: false});
        this.carousel.current.next();
      },
    });
  }

  stepTwoSubmit(code) {
    this.setState({code_invalid: false});
    this.props.validateCode({
      code,
      type: VerificationCodeType.ACTIVATE_ACCOUNT,
      callback: () => {
        this.setState({code});
        this.carousel.current.next();
      },
      onError: () => {
        this.setState({code_invalid: true});
      },
    });
  }

  stepTwoResend() {
    this.carousel.current.prev();
    this.setState({code: defaultState.code});
  }

  stepThreeSubmit(event) {
    event.preventDefault();
    this.setState({stepThreeInProgress: true});
    this.props.signUp({
      ...Object.fromEntries(new FormData(event.target)),
      activationCode: this.state.code,
      callback: () => {
        this.props.openModal({
          id: LoginModalId,
          message: {type: 'success', text: this.props.t('modals.signup.accountCreated')},
        });
        this.cleanup();
      },
    });
  }

  onCancel() {
    this.props.closeModal();
    this.cleanup();
  }

  onUserAgreementClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.props.openModal({
      id: USER_AGREEMENT_MODAL_ID,
      prevOpened: SignUpModalId,
    });
  }

  cleanup() {
    this.setState(defaultState);
    this.carousel.current.goTo(0);
  }

  onCodeChange(code) {
    this.setState({code});
  }

  render() {
    return (
      <Modal
        title={<TranslatedText transKey="modals.signup.title"/>}
        className="nav-modal signup-modal"
        width={440}
        visible={this.props.opened}
        onCancel={this.onCancel}
        closeIcon={<CloseIcon/>}
        footer={<React.Fragment>
          <span><TranslatedText transKey="modals.common.alreadyHaveAccount"/></span>
          <a onClick={() => this.props.openModal({id: LoginModalId})}>
            <TranslatedText transKey="modals.common.loginLink"/>
            <ArrowLeft className="action-icon"/>
          </a>
        </React.Fragment>
        }
      >
        <Carousel ref={this.carousel} dots={false} infinite={false}>
          <div id="signup-email-slide">
            <div className="stage-indicator">
              <h3><TranslatedText transKey="modals.common.step" params={{curr: 1, max: 2}}/></h3>
              <p><TranslatedText transKey="modals.signup.enterEmail"/></p>
            </div>
            <form onSubmit={this.stepOneSubmit}>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="email">
                  <Input placeholder={this.props.t('modals.signup.emailPlaceholder')} name="email" value={this.state.email} onChange={e => this.setState({email: e.target.value})}/>
                </FormError.Tooltip>
                <FormError.Tooltip errors={this.props.errors} fieldName="agreed">
                  <Checkbox checked={this.state.agreed} onChange={() => this.setState({agreed: !this.state.agreed})}>
                    <Trans i18nKey="modals.signup.userAgreement">
                      modals.signup.ihaveread<a onClick={this.onUserAgreementClick}>useragreement</a>
                    </Trans>
                  </Checkbox>
                </FormError.Tooltip>
                <Button type="primary" block htmlType="submit" loading={this.state.stepOneInProgress}>
                  <TranslatedText transKey="modals.verification.next"/>
                </Button>
                <div className="submit-subelement">
                  <a onClick={() => this.carousel.current.next()}>
                    <TranslatedText transKey="modals.verification.ihavecode"/>
                  </a>
                </div>
              </div>
            </form>
          </div>
          <div id="signup-activation-code-slide">
            <VerificationCodeForm onChange={this.onCodeChange} code={this.state.code} onSubmit={this.stepTwoSubmit} isInvalid={this.state.code_invalid}>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.verification.next"/>
              </Button>
              <div className="submit-subelement">
                <a onClick={this.stepTwoResend}>
                  <ArrowRight className="action-icon right"/>
                  <TranslatedText transKey="modals.common.startover"/>
                </a>
              </div>
            </VerificationCodeForm>
          </div>
          <div id="signup-form-slide">
            <div className="recovery-stage-indicator">
              <h3><TranslatedText transKey="modals.common.step" params={{curr: 2, max: 2}}/></h3>
              <p><TranslatedText transKey="modals.signup.enterCredentials"/></p>
            </div>
            <form onSubmit={this.stepThreeSubmit}>
              <FormError errors={this.props.errors} fieldName="non_field_errors"/>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="username">
                  <Input type="text" placeholder={this.props.t('modals.signup.username')} name="username" required/>
                </FormError.Tooltip>
              </div>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="password">
                  <Input type="password" placeholder={this.props.t('modals.signup.password')} name="password" required suffix={
                     <Tooltip title={this.props.t('modals.profile.password.info')} placement="right">
                       <Icon type="info-circle"/>
                     </Tooltip>
                     }/>
                </FormError.Tooltip>
              </div>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="passwordRepeat">
                  <Input type="password" placeholder={this.props.t('modals.signup.passwordRepeat')} name="passwordRepeat" required/>
                </FormError.Tooltip>
              </div>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="wallet">
                  <Input type="text" placeholder={this.props.t('modals.signup.wallet')} name="wallet"/>
                </FormError.Tooltip>
              </div>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.signup.submit"/>
              </Button>
            </form>
          </div>
        </Carousel>
      </Modal>
    );
  }
}

SignUpModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  requestActivationCode: PropTypes.func.isRequired,
  signUp: PropTypes.func.isRequired,
  validateCode: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  opened: isModalOpened(state)(SignUpModalId),
  errors: authErrors(state),
});

const mapDispatchToProps = {
  closeModal: makeAction(CLOSE_MODAL),
  openModal: makeAction(OPEN_MODAL),
  requestActivationCode: makeAction(REQUEST_ACTIVATION_CODE),
  signUp: makeAction(SIGN_UP),
  validateCode: makeAction(VALIDATE_CODE),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SignUpModal));



// WEBPACK FOOTER //
// /src/components/modals/SignUpModal.jsx